import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, FileQuestion, Settings,
  CreditCard, LogOut, Menu, X, ChevronRight, ExternalLink, ArrowLeft
} from 'lucide-react';
import DashboardHome from '../components/DashboardHome';
import ApplicantsList from '../components/ApplicantsList';
import FormBuilder from '../components/FormBuilder';
import AgencySettings from '../components/AgencySettings';
import SubscriptionManager from '../components/SubscriptionManager';

const NAV_ITEMS = [
  { id: 'home', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'applicants', label: 'Applicants', icon: Users },
  { id: 'form', label: 'Application Form', icon: FileQuestion },
  { id: 'subscription', label: 'Subscription', icon: CreditCard },
  { id: 'settings', label: 'Settings', icon: Settings },
] as const;

type NavId = typeof NAV_ITEMS[number]['id'];

const APP_BASE = window.location.origin;

export default function DashboardPage({ initialTab }: { initialTab?: string | null }) {
  const { agency, user } = useAuth();
  const [activeTab, setActiveTab] = useState<NavId>(initialTab === 'subscription' ? 'subscription' : 'home');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  async function signOut() {
    await supabase.auth.signOut();
  }

  const applyLink = `${APP_BASE}/apply/${agency?.slug}`;

  function renderContent() {
    switch (activeTab) {
      case 'home': return <DashboardHome />;
      case 'applicants': return <ApplicantsList />;
      case 'form': return <FormBuilder />;
      case 'subscription': return <SubscriptionManager />;
      case 'settings': return <AgencySettings />;
    }
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar overlay on mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-100 flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-5 py-5 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{agency?.name ?? 'CareHire'}</p>
            <p className="text-xs text-gray-400">Agency Dashboard</p>
          </div>
          <button
            className="lg:hidden text-gray-400 hover:text-gray-600"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Apply link quick access */}
        <div className="px-3 py-3 border-b border-gray-100">
          <a
            href={applyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
            <span className="text-xs text-blue-700 font-medium truncate flex-1">{applyLink.replace(/^https?:\/\//, '')}</span>
          </a>
        </div>

        {/* Back to site */}
        <div className="px-3 pt-3">
          <button
            onClick={async () => { await supabase.auth.signOut(); window.location.href = '/'; }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => { setActiveTab(id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-blue-700 text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
              {activeTab === id && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
            </button>
          ))}
        </nav>

        {/* Bottom: user + sign out */}
        <div className="px-3 py-3 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-700 font-bold text-xs">
                {user?.email?.charAt(0).toUpperCase() ?? 'U'}
              </span>
            </div>
            <p className="text-xs text-gray-500 truncate flex-1">{user?.email}</p>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar (mobile) */}
        <header className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-9 h-9 rounded-md border border-gray-200 flex items-center justify-center text-gray-600"
          >
            <Menu className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <span className="font-bold text-gray-900 text-sm">{agency?.name ?? 'CareHire'}</span>
          </div>
          <span className="text-sm text-gray-500 font-medium">
            {NAV_ITEMS.find((n) => n.id === activeTab)?.label}
          </span>
        </header>

        {/* Desktop top bar */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 bg-white border-b border-gray-100">
          <div>
            <p className="text-sm font-bold text-gray-900">
              {NAV_ITEMS.find((n) => n.id === activeTab)?.label}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={applyLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors border border-blue-100"
            >
              <ExternalLink className="w-3 h-3" />
              View apply page
            </a>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-5 lg:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
